/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useState } from 'react';
import { Button, Dialog, DialogActions, Grid, MenuItem } from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { CustomDialogTitle } from 'src/components';
import { useStyles } from '../styles';
import { PartnerPartnership } from '../../../../models';
import { useCommonStyles } from '../../../../styles';
import { FieldLabel } from '../../../Form';
import { MultiSelectControl } from '../../../Controls';
import { CheckIcon } from '../../../CustomIcons';
import { getPartnersLogins } from '../ReportGeneralToolbarWithFiltersService';
import { SpinnerWithProps } from '../../../SpinnerWithProps';
import { useApiError, useAppData } from '../../../../providers';

export interface Props {
  open: boolean;
  translation: any;
  dateRange: DateRange<Date>;
  partnerships: PartnerPartnership[];
  onClose: () => void;
  applyFilter: (partnerships: string[], logins: string[]) => void;
  renderValueCustomCondition: (value: string[]) => void;
}

export const FiltersDialog: FC<Props> = ({ open, dateRange, partnerships, translation, onClose, applyFilter, renderValueCustomCondition }) => {
  const classes = useStyles();
  const { userData } = useAppData();
  const { addError } = useApiError();
  const commonClasses = useCommonStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [logins, setLogins] = useState<any[]>([]);
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const [selectedLogins, setSelectedLogins] = useState<string[]>(['All']);

  const applyNewFilter = () => {
    applyFilter(selectedPartnerships, selectedLogins);
  };

  const clearFilter = () => {
    setSelectedPartnerships(['All']);
    setSelectedLogins(['All']);
    applyFilter(['All'], ['All']);
  };

  const handleClose = () => {
    onClose();
  };

  const getLogins = useCallback(
    async (partnershipCodes: string[]) => {
      const userLoginData = JSON.parse(userData);
      try {
        setLoading(true);
        const response = await getPartnersLogins(moment(dateRange[0]).format('YYYY-MM-DD'), moment(dateRange[1]).format('YYYY-MM-DD'), partnershipCodes, userLoginData.partnerId);
        setLogins(response.data);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setLoading(false);
      }
    },
    [dateRange],
  );

  const handlePartnershipSelectChange = useCallback(async (event) => {
    let options = event.target.value as string[];
    if (options.length === 0) {
      options = ['All'];
    }

    if (options.length > 1) {
      if (options.includes('All')) {
        if (options[options.length - 1] === 'All') {
          options = options.filter((option) => option === 'All');
        } else {
          options = options.filter((option) => option !== 'All');
        }
      }
    }

    setSelectedPartnerships(options);
    await getLogins(options.includes('All') ? [] : options);
  }, []);

  const handleLoginSelectChange = useCallback(async (event) => {
    let options = event.target.value as string[];
    if (options.length === 0) {
      options = ['All'];
    }

    if (options.length > 1) {
      if (options.includes('All')) {
        if (options[options.length - 1] === 'All') {
          options = options.filter((option) => option === 'All');
        } else {
          options = options.filter((option) => option !== 'All');
        }
      }
    }

    setSelectedLogins(options);
  }, []);

  return (
    <Dialog onClose={handleClose} open={open} classes={{ paper: classes.dialog }}>
      <SpinnerWithProps isLoading={loading} />
      <CustomDialogTitle title={translation.filters} onClose={() => onClose()} />
      <Grid container direction="column">
        {partnerships && partnerships.length !== 0 && (
          <Grid item>
            <FieldLabel label={translation.text_6447} />
            <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selectedPartnerships} onChange={(event: any) => handlePartnershipSelectChange(event)}>
              <MenuItem className={selectedPartnerships.includes('All') ? classes.selectedOption : classes.option} value="All">
                <CheckIcon />
                <span>{translation.all}</span>
              </MenuItem>
              {partnerships.map((partnership) => (
                <MenuItem
                  className={selectedPartnerships.includes(partnership.partnershipCode as string) ? classes.selectedOption : classes.option}
                  key={partnership.partnershipCode}
                  value={partnership.partnershipCode ? partnership.partnershipCode : ''}
                >
                  <CheckIcon />
                  <span>{partnership.partnershipCode}</span>
                </MenuItem>
              ))}
            </MultiSelectControl>
          </Grid>
        )}
        {selectedPartnerships.length > 0 && (
          <Grid item style={{ marginTop: 10 }}>
            <FieldLabel label={translation.text_6001} />
            <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selectedLogins} onChange={(event: any) => handleLoginSelectChange(event)}>
              <MenuItem className={selectedLogins.includes('All') ? classes.selectedOption : classes.option} value="All">
                <CheckIcon />
                <span>{translation.all}</span>
              </MenuItem>
              {logins.map((login) => (
                <MenuItem className={selectedLogins.includes(login.key) ? classes.selectedOption : classes.option} key={login.key} value={login.key}>
                  <CheckIcon />
                  <span>{login.key}</span>
                </MenuItem>
              ))}
            </MultiSelectControl>
          </Grid>
        )}
      </Grid>
      <DialogActions className={commonClasses.dialogAction}>
        <Button onClick={applyNewFilter} fullWidth startIcon={<Check />} className={commonClasses.activeDialogBtn}>
          {translation.text_65}
        </Button>
        <Button onClick={clearFilter} fullWidth startIcon={<Close />} className={commonClasses.cancelDialogBtn}>
          {translation.clearAll}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
